/**
 * Smart routing for whatever the camera reads.
 *
 * The scanner in Food Scanner reads both 1D barcodes and QR codes. The caller
 * passes the raw string we got from ZXing; we decide where it should go:
 *   • barcode       — pure digits → stay in the Food Scanner (product lookup)
 *   • recipe_url    — a URL on a known recipe or meal-kit site → send to Recipes importer
 *   • unknown_url   — any other http(s) URL → let the caller ask the user
 *   • text          — anything else (plain text QR, nothing we can route on)
 */

// Meal-kit cards usually point at the mobile app via a deeplink, or at a PDF
// hosted on S3 (`product-card-service-live.s3.*`). We translate those to the
// equivalent public recipe page so our existing `/recipes/parse` endpoint can
// pull the ingredients + macros (the backend auto-detects PDF content).
const RECIPE_DOMAINS = [
  "hellofresh.co.uk", "hellofresh.com", "hellofresh.ca", "hellofresh.de",
  "product-card-service-live.s3", // HelloFresh dynamic recipe-card PDFs
  "gousto.co.uk",
  "mindfulchef.com",
  "greenchef.co.uk", "greenchef.com",
  "bbcgoodfood.com",
  "bbc.co.uk/food",
  "allrecipes.com", "allrecipes.co.uk",
  "jamieoliver.com",
  "deliciousmagazine.co.uk",
  "foodnetwork.com", "foodnetwork.co.uk",
  "sainsburysmagazine.co.uk",
  "tesco.com/recipes",
  "nytimes.com/recipes",
  "simplyrecipes.com",
  "seriouseats.com",
  "budgetbytes.com",
  "cookpad.com",
  "yummly.com",
  "epicurious.com",
];

const normaliseHelloFreshDeeplink = (raw) => {
  // hellofresh://recipes/<slug> or hellofresh://recipe/<slug>
  const m = /^hellofresh:\/\/(?:recipes?|r)\/([a-z0-9-]+)/i.exec(raw.trim());
  if (m) return `https://www.hellofresh.co.uk/recipes/${m[1]}`;
  // Short links we might see on new cards.
  const short = /^https?:\/\/hf\.co\/r\/([a-z0-9-]+)/i.exec(raw.trim());
  if (short) return `https://www.hellofresh.co.uk/recipes/${short[1]}`;
  return null;
};

const isNumericBarcode = (raw) => /^\d{6,14}$/.test(raw.trim());

export function routeScan(raw) {
  const value = String(raw || "").trim();
  if (!value) return { kind: "text", value };

  if (isNumericBarcode(value)) {
    return { kind: "barcode", value };
  }

  const hfDeeplink = normaliseHelloFreshDeeplink(value);
  if (hfDeeplink) {
    return { kind: "recipe_url", url: hfDeeplink, source: "hellofresh" };
  }

  // Must look like a URL from here on.
  let parsed;
  try {
    parsed = new URL(value);
  } catch {
    return { kind: "text", value };
  }
  if (!/^https?:$/.test(parsed.protocol)) {
    return { kind: "text", value };
  }

  const hostAndPath = (parsed.host + parsed.pathname).toLowerCase();
  const match = RECIPE_DOMAINS.find((d) => hostAndPath.includes(d));
  if (match) {
    return { kind: "recipe_url", url: value, source: match };
  }
  // Any PDF link is almost certainly a printable recipe card.
  if (parsed.pathname.toLowerCase().endsWith(".pdf")) {
    return { kind: "recipe_url", url: value, source: "pdf" };
  }

  return { kind: "unknown_url", url: value };
}
